// src/modules/reports/components/ReportsListView.tsx
import { useEffect, useState, useMemo } from "react";
import { useReport } from "../hooks/use-report.hook";
import ReportTable from "./table/ReportTable";
import { Report } from "../models/report.model";
import Pagination from "components/shared/Pagination/Pagination";
import { REPORT_STATUS, REPORT_TYPE } from "../enums/report.enum";
import { useFilters } from "../hooks/useFilters";
import { useDebounce } from "../hooks/useDebounce";
import { SearchInput } from "components/shared/SearchInput/SearchInput";
import { FilterBar } from "components/shared/FilterBar/FilterBar";
import { buildReportFilterFields } from "../configs/reportFilter.config";
import { ReportTableSkeleton } from "./ReportSkeleton";

const PAGE_SIZE = 10;
const FETCH_LIMIT = 100;

export default function ReportsListView() {
  const { getReport } = useReport();

  const { filters, updateFilter } = useFilters<{
    status: REPORT_STATUS | "";
    report_type: REPORT_TYPE | "";
  }>({
    status: "",
    report_type: "",
  });

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);

  const [reportList, setReportList] = useState<Report[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReport, setTotalReport] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset về trang 1 khi filter/search thay đổi
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  const loadReports = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getReport(
        FETCH_LIMIT,
        1,
        filters.status || "",
        filters.report_type || "",
        debouncedSearch,
      );

      let reports = response.data || [];

      reports = reports.filter((report: Report) => {
        return !report.deleted_at && report.target_display !== "Đã xóa";
      });

      reports.sort((a: Report, b: Report) => {
        if (
          a.status === REPORT_STATUS.PENDING &&
          b.status !== REPORT_STATUS.PENDING
        )
          return -1;
        if (
          a.status !== REPORT_STATUS.PENDING &&
          b.status === REPORT_STATUS.PENDING
        )
          return 1;
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      setReportList(reports);
      setTotalReport(reports.length);
    } catch {
      setError("Không thể tải danh sách báo cáo. Vui lòng thử lại.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [filters, debouncedSearch]);

  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return reportList.slice(start, start + PAGE_SIZE);
  }, [reportList, currentPage]);

  const totalPages = Math.ceil(reportList.length / PAGE_SIZE) || 1;

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  if (error) {
    return (
      <div className="text-center py-5">
        <p className="text-danger">{error}</p>
        <button className="btn btn-primary" onClick={loadReports}>
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div id="reportsListView" className="text-capitalize mt-2">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Tìm theo ID hoặc người báo cáo..."
          width="300px"
        />
        <FilterBar
          filters={filters}
          fields={buildReportFilterFields()}
          onChange={updateFilter}
          ignoreActiveKeys={[]}
        />
      </div>

      {isLoading ? (
        <ReportTableSkeleton />
      ) : (
        <>
          <ReportTable reportList={paginatedReports} />
          <div className="d-flex justify-content-between align-items-center mt-4">
            <span className="text-muted me-3">
              Hiển thị {(currentPage - 1) * PAGE_SIZE + 1} -{" "}
              {Math.min(currentPage * PAGE_SIZE, totalReport)} trong tổng số{" "}
              {totalReport}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
}
